import { env } from '@src/config/env';
import { reportNetworkError } from '@src/context/NetworkStatusContext';

// fetch() only rejects on network failures (e.g. offline, DNS, CORS) — HTTP
// error statuses resolve normally and are handled by the `!res.ok` checks
// below. Piggyback on that rejection to report offline immediately, per the
// zero-polling network detection strategy (API-4377).
export async function fetchWithNetworkReporting(
    input: RequestInfo | URL,
    init?: RequestInit
): Promise<Response> {
    try {
        return await fetch(input, init);
    } catch (err) {
        reportNetworkError();
        throw err;
    }
}

// --- /api/v2/companies ---

interface ApiCompanyListItem {
    id: number;
    name: string;
    status?: string;
    photo?: string | null;
}

export interface CompanyListItem {
    id: number;
    name: string;
    logoUrl?: string;
}

export async function fetchCompanies(
    accessToken: string
): Promise<CompanyListItem[]> {
    const res = await fetchWithNetworkReporting(`${env.apiBaseUrl}/api/v2/companies`, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json'
        }
    });

    if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(
            `Companies fetch failed (${res.status}): ${body || res.statusText}`
        );
    }

    const json = await res.json();
    const items: ApiCompanyListItem[] = json.data ?? [];
    return items
        .filter(c => c.status === 'active' || c.status === 'trial')
        .map(c => ({
            id: c.id,
            name: c.name,
            logoUrl: c.photo
                ? new URL(c.photo, env.webappBaseUrl).href
                : undefined
        }));
}

// --- /api/v2/company/{company_id}/locations ---

interface ApiLocationListItem {
    id: number;
    name: string;
    hash: string;
    timezone?: string;
}

export interface LocationListItem {
    id: number;
    name: string;
    hash: string;
    timezone: string;
}

export async function fetchLocations(
    companyId: number,
    accessToken: string
): Promise<LocationListItem[]> {
    const res = await fetchWithNetworkReporting(
        `${env.apiBaseUrl}/api/v2/company/${companyId}/locations?limit=500&deleted=false`,
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: 'application/json'
            }
        }
    );

    if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(
            `Locations fetch failed (${res.status}): ${body || res.statusText}`
        );
    }

    const json = await res.json();
    const items: ApiLocationListItem[] = json.data ?? [];
    return items.map(l => ({
        id: l.id,
        name: l.name,
        hash: l.hash,
        timezone: l.timezone ?? 'UTC'
    }));
}

// --- /api/v2/company/{company_id}/account ---

interface ApiCompany {
    id?: number;
    name?: string;
}

interface ApiAccountResponse {
    user_id?: number;
    user_name?: string;
    first_name?: string;
    last_name?: string;
    punch_id?: string | null;
    company_id?: number;
    company_name?: string;
    company?: ApiCompany;
    location?: { hash?: string };
}

export interface AccountData {
    userId: number;
    userName: string;
    /**
     * Punch ID of the authenticated user (the admin who logged the terminal
     * in). Used to gate destructive Settings actions (API-4551). Null when the
     * account has no punch ID configured.
     */
    punchId: string | null;
    companyId: number;
    companyName: string;
    fallbackLocationHash: string;
}

export async function fetchAccount(
    companyId: number,
    accessToken: string
): Promise<AccountData> {
    const res = await fetchWithNetworkReporting(
        `${env.apiBaseUrl}/api/v2/company/${companyId}/account`,
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: 'application/json'
            }
        }
    );

    if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(
            `Account fetch failed (${res.status}): ${body || res.statusText}`
        );
    }

    const json = await res.json();
    const data: ApiAccountResponse = json.data ?? json;

    const userName =
        data.user_name ??
        [data.first_name, data.last_name].filter(Boolean).join(' ') ??
        '';

    return {
        userId: data.user_id ?? 0,
        userName,
        punchId: data.punch_id ?? null,
        companyId: data.company_id ?? companyId,
        companyName: data.company?.name ?? data.company_name ?? '',
        fallbackLocationHash: data.location?.hash ?? ''
    };
}
